import React, { useState, useEffect } from 'react';
import styled from 'styled-components';

import { Icon } from '@ui/icons/Icon.tsx';

import { useCSSColors } from '../../../../src/hooks/useCSSColors.ts';

const FabButton = styled.button`
  cursor: pointer;
  background: var(--color-background);
  box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.2);
  border-radius: 100%;
  outline: none;
  border: none;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: space-around;
  &:focus-visible {
    outline: var(--focus-outline);
    outline-offset: 2px;
  }
`;

const FollowMeCaption = styled.div`
  position: absolute;
  color: var(--color-toolTipText);
  font-size: 14px;
  line-height: 16px;
  inset-inline-end: 58px;
  white-space: nowrap;
  padding: 6px;
  box-shadow: 0px 2px 12px rgba(0, 0, 0, 0.2);
  background: var(--color-toolTipBackground);
  border-radius: 4px;
`;

// widgetState properties:
//  isMonitoring (true if device supports and user has allowed)
//  hasLocation (true if we have a "current" location within venue)
//  isFollowing (map is tracking bluedot - implies other two are true)

const FollowMeButton = ({ widgetState, onClick }) => {
  const colors = useCSSColors();
  const [state, setState] = useState(widgetState.getState());
  useEffect(() => widgetState.addCallback(setState), [widgetState]);

  // FollowingMe button states (from spec)
  //   Active - when the user position is available and isFollowing.
  //   Unfocused - when the user position is available and !isFollowing
  //   Inactive - When the user position was available but was lost (isMonitoring && !hasLocation)
  //   Unavailable - When the user position was never available during this session (!isMonitoring)

  const buttonStatus = !state.isMonitoring
    ? 'Unavailable'
    : !state.hasLocation
      ? 'Inactive'
      : state.isFollowing
        ? 'Active'
        : 'Unfocused';

  const color =
    buttonStatus === 'Active' || buttonStatus.status === 'Unavailable'
      ? colors.primaryButton // blue
      : buttonStatus === 'Inactive'
        ? colors.secondaryText // gray
        : 'var(--color-secondaryText-50)'; // v. light gray ("Unfocused")

  const iconName = buttonStatus === 'Unavailable' ? 'my-location-arrow-stroke' : 'my-location-arrow';

  return (
    <FabButton onClick={onClick} data-testid="FollowMeButton" aria-label={'Follow Me'}>
      {state.followMeCaption && <FollowMeCaption>{state.followMeCaption}</FollowMeCaption>}
      <Icon name={iconName} cdn fillColor={color} height={32} width={32} />
    </FabButton>
  );
};

export default FollowMeButton;
